import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * usePlacedSharedLists — given the placement docs targeting a board,
 * subscribes to each underlying shared-list doc (under its owner's
 * subcollection) and returns the merged array of resolved list docs.
 *
 * Walter 2026-05-11: this is the read side of the "place a shared
 * list in your own board" mechanic. The placement record holds a
 * pointer to where the list actually lives (sourceOwnerUid /
 * sourceBoardId / listId); this hook resolves those pointers into
 * real list documents that BoardPage / MobileBoard can render
 * alongside the user's own lists.
 *
 * Each list is annotated with `_sharedFromOwnerUid` and
 * `_placementId` so downstream code (panels, sidebar, edit gates)
 * can tell it apart from a normal own-board list. If the source
 * list has been deleted (or the user lost access), the slot drops
 * out of the result quietly — orphan placements are hidden rather
 * than rendered as ghost rows. Lazy cleanup of those orphan
 * placement docs is deferred (Walter's brief: "they're harmless
 * if hidden").
 *
 * Listeners are opened per-placement and cleaned up on placement
 * removal or unmount.
 */
export function usePlacedSharedLists(placementsForBoard) {
  // Map from a stable key (sourceOwnerUid/sourceBoardId/listId) to
  // the live resolved list doc. Stored as an object rather than an
  // array so per-listener updates can write a single slot without
  // re-traversing.
  const [resolved, setResolved] = useState({});

  useEffect(() => {
    const placements = Array.isArray(placementsForBoard) ? placementsForBoard : [];
    if (placements.length === 0) {
      setResolved({});
      return undefined;
    }

    // Track which keys are active in this effect cycle so we can
    // clean up resolved entries that no longer correspond to a
    // placement when the placements list shrinks.
    const activeKeys = new Set();
    const unsubs = [];

    for (const p of placements) {
      const { id: listId, sourceOwnerUid, sourceBoardId, boardId } = p;
      if (!listId || !sourceOwnerUid || !sourceBoardId || !boardId) continue;
      const key = `${sourceOwnerUid}/${sourceBoardId}/${listId}`;
      activeKeys.add(key);
      const ref = doc(db, 'users', sourceOwnerUid, 'boards', sourceBoardId, 'lists', listId);
      const unsub = onSnapshot(
        ref,
        (snap) => {
          setResolved(prev => {
            const next = { ...prev };
            if (!snap.exists()) {
              // Orphan: owner deleted the list or revoked our share.
              // Drop it from the resolved set so the canvas doesn't
              // render a ghost row.
              delete next[key];
              return next;
            }
            const sourceData = snap.data();
            // Walter 2026-05-11: per-invitee positional override.
            // Where the placement doc has x/y/w/h/etc., those win
            // over the owner's values — so the placed list sits
            // where the invitee put it, independent of how the
            // owner has positioned it on their own canvas. When the
            // placement doc has no positional fields (v1 placements
            // shipped before the override schema landed, or before
            // the user ever moved the list), we fall through to the
            // owner's values.
            const merged = { ...sourceData };
            if (typeof p.x === 'number') merged.x = p.x;
            if (typeof p.y === 'number') merged.y = p.y;
            if (typeof p.w === 'number') merged.w = p.w;
            if (typeof p.h === 'number') merged.h = p.h;
            if (typeof p.z === 'number') merged.z = p.z;
            if (typeof p.gridCol === 'number') merged.gridCol = p.gridCol;
            if (typeof p.gridRow === 'number') merged.gridRow = p.gridRow;
            if (typeof p.order === 'number') merged.order = p.order;
            next[key] = {
              id: snap.id,
              ...merged,
              _sharedFromOwnerUid: sourceOwnerUid,
              _sharedFromBoardId: sourceBoardId,
              // Owner name cached on the placement record (best-effort
              // — empty when unknown). Drives the "shared by <name>"
              // band on ListPanel + FocusedPanel.
              _sharedFromOwnerName: p.sourceOwnerName || '',
              // Title snapshot at place-time. Used as a fallback when
              // the owner's live title is empty so the invitee
              // doesn't see "(empty)" for a list someone shared with
              // them. Live title still wins when non-empty.
              _sharedFromCachedTitle: p.sourceCachedTitle || '',
              _placementId: listId,
              _placementTargetBoardId: boardId,
            };
            return next;
          });
        },
        (err) => {
          // Permission denial here means the user has been revoked
          // from the share (lazy cleanup path, per Walter's brief).
          // Drop the slot quietly and let the next "share with me
          // again" round restore it. We don't auto-delete the
          // placement doc — it's harmless if hidden, and avoids
          // racing the rules' re-propagation after a flaky revoke.
          console.warn('[usePlacedSharedLists] snapshot error', { listId, err });
          setResolved(prev => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          });
        },
      );
      unsubs.push(unsub);
    }

    // Synchronously prune any resolved entries whose key is no longer
    // in the active set. Without this, removing a placement leaves
    // its last-known list doc lingering until React garbage-collects
    // the unsub (which can outlast the next render).
    setResolved(prev => {
      let changed = false;
      const next = {};
      for (const k of Object.keys(prev)) {
        if (activeKeys.has(k)) next[k] = prev[k];
        else changed = true;
      }
      return changed ? next : prev;
    });

    return () => {
      for (const unsub of unsubs) {
        try { unsub(); } catch {}
      }
    };
    // We deliberately depend on the placements array reference. The
    // caller passes the same array reference until the placement
    // collection changes, so this effect doesn't churn on unrelated
    // BoardPage re-renders.
  }, [placementsForBoard]);

  // Walter 2026-05-12: memoize the array so its identity is stable
  // across renders when `resolved` hasn't changed. Without this, the
  // hook returned a fresh array on every BoardPage render, which
  // churned handleUpdate's useCallback identity, which churned
  // ListPanel.flushSave's useCallback identity, whose effect cleanup
  // runs flushSave() unconditionally — defeating the 4s save
  // debounce and firing an immediate save on every keystroke (since
  // each keystroke triggers onSaveStatus('saving') → BoardPage
  // setState → re-render → new array).
  return useMemo(() => Object.values(resolved), [resolved]);
}
